/**
 * EditHistoryPanel (iter-39) — visible timeline of the last 20
 * structural / manual-compose actions with one-click revert per row.
 * Renders as a collapsible sidebar card so it doesn't dominate the
 * editor. Uses the ``useEditHistory`` hook (fetch + record + revert).
 */
import React, { useState } from "react";
import { toast } from "sonner";
import { History, RotateCcw, ChevronDown, ChevronRight } from "lucide-react";

// Human-readable label + subtle color per action slug. Unknown actions
// fall back to a neutral "Edit" tag so authors still see the row.
const _ACTION_META = {
  chapter_rename:      { label: "Rename",   color: "text-slate-700" },
  chapter_reorder:     { label: "Reorder",  color: "text-indigo-700" },
  chapter_delete:      { label: "Delete",   color: "text-red-700" },
  chapter_reclassify:  { label: "Reclassify", color: "text-amber-700" },
  structure_role:      { label: "Role change", color: "text-blue-700" },
  structure_bulk_role: { label: "Bulk role", color: "text-blue-700" },
  structure_reset:     { label: "Reset roles", color: "text-blue-700" },
  manual_save:         { label: "Manual save", color: "text-emerald-700" },
  kind_change:         { label: "Kind change", color: "text-purple-700" },
  revert:              { label: "Revert",   color: "text-orange-700" },
  edit:                { label: "Edit",     color: "text-slate-700" },
};

function _fmtWhen(iso) {
  try {
    const d = new Date(iso);
    const now = new Date();
    const ms = now - d;
    const s = Math.round(ms / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.round(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    return d.toLocaleDateString();
  } catch { return iso || ""; }
}

export function EditHistoryPanel({ history, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const entries = history?.entries || [];
  const topEntry = entries[0];

  const doRevert = async (entry) => {
    if (!window.confirm(`Revert this edit? It will undo:\n\n"${entry.description || entry.action}"\n\nYou can undo the revert from this timeline afterwards.`)) return;
    try {
      await history.revert(entry.id);
      toast.success(`Reverted: ${entry.description || entry.action}`);
    } catch (err) {
      toast.error(`Revert failed: ${err?.message || "unknown error"}`);
    }
  };

  return (
    <div className="border border-[color:var(--border)] bg-white" data-testid="edit-history-panel">
      <button
        type="button"
        onClick={()=>setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[color:var(--canvas)]/40"
        data-testid="edit-history-toggle"
      >
        <span className="flex items-center gap-2">
          <History className="w-4 h-4" strokeWidth={1.5}/>
          <span className="text-xs uppercase tracking-[0.25em] text-[color:var(--text-muted)]">
            § Edit history
          </span>
          {entries.length > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 border border-[color:var(--border)] bg-[color:var(--canvas)]"
                  data-testid="edit-history-count">{entries.length}</span>
          )}
        </span>
        {open ? <ChevronDown className="w-4 h-4"/> : <ChevronRight className="w-4 h-4"/>}
      </button>

      {/* Undo-last shortcut visible even when collapsed. */}
      {topEntry && !open && (
        <div className="px-4 pb-3 flex items-center justify-between text-xs">
          <span className="text-[color:var(--text-muted)] truncate mr-2" title={topEntry.description}>
            Last: {topEntry.description || topEntry.action}
          </span>
          <button
            type="button"
            onClick={()=>doRevert(topEntry)}
            disabled={history.reverting === topEntry.id}
            className="btn-ghost text-xs inline-flex items-center gap-1 shrink-0"
            data-testid="edit-history-undo-last"
          >
            <RotateCcw className="w-3.5 h-3.5"/> Undo
          </button>
        </div>
      )}

      {open && (
        <div className="border-t border-[color:var(--border)] max-h-[420px] overflow-y-auto"
             data-testid="edit-history-list">
          {entries.length === 0 ? (
            <p className="px-4 py-6 text-xs text-[color:var(--text-muted)] italic text-center">
              No edits yet. Chapter renames, reorder, delete, role/kind changes and
              Manual Compose saves will appear here so you can undo any of them.
            </p>
          ) : (
            <ul className="divide-y divide-[color:var(--border)]">
              {entries.map((e, i) => {
                const meta = _ACTION_META[e.action] || _ACTION_META.edit;
                return (
                  <li key={e.id}
                      className="px-4 py-2.5 flex items-start gap-3 text-xs"
                      data-testid={`edit-history-row-${i}`}
                      data-action={e.action}>
                    <div className="flex-1 min-w-0">
                      <p className="flex items-center gap-2">
                        <span className={`uppercase tracking-widest text-[10px] font-semibold ${meta.color}`}>
                          {meta.label}
                        </span>
                        <span className="text-[10px] text-[color:var(--text-muted)]">
                          {_fmtWhen(e.created_at)}
                        </span>
                      </p>
                      <p className="mt-0.5 text-[color:var(--text)] break-words" title={e.description}>
                        {e.description || <em className="text-[color:var(--text-muted)]">(no description)</em>}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={()=>doRevert(e)}
                      disabled={history.reverting === e.id}
                      className="btn-ghost text-[10px] inline-flex items-center gap-1 shrink-0"
                      data-testid={`edit-history-revert-${i}`}
                      title="Restore the book to the state before this edit"
                    >
                      <RotateCcw className="w-3.5 h-3.5"/>
                      {history.reverting === e.id ? "…" : "Revert"}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
